import Luiv3Constants from '@app/lui3/constants';
import {useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';

import {
  addDebouncedFocusVisibleRefetch,
  isAbortError,
} from '../../../browserRefetch';
import {
  fetchOutlookPeopleFromEmailAddresses,
  type OutlookPeopleFromEmailAddressesJson,
  type OutlookPersonFromEmailJson,
} from '../../../requests';
import {usePodRequest} from '../../../usePodRequest';
import {
  emailMatchesSet,
  normalizedEmailKey,
  normalizeEmailAddress,
  normalizeEmailVariants,
} from './emailNormalization';
import InboundSyncButton from './InboundSyncButton';
import LoxoRecipients from './LoxoRecipients';
import OtherRecipients from './OtherRecipients';
import {
  type OutlookRecipient,
  useOutlookEmailRecipients,
} from './useOutlookEmailRecipients';

interface Props {
  agencyId: number;
}

const OuterWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-grow: 1;
  flex-direction: column;
`;

const InnerWrapper = styled.div`
  padding: 1rem;
  display: flex;
  gap: 1rem;
  flex-direction: column;
`;

const TotalCount = styled.div`
  font-size: 0.875rem;
  color: ${Luiv3Constants.baseColor['gray-1000']};
  font-weight: 700;
  line-height: 1.25rem;
  letter-spacing: 0.01em;
`;

const dedupeRecipientsByEmail = (
  recipients: OutlookRecipient[]
): OutlookRecipient[] => {
  const seen = new Set<string>();
  return recipients.filter((recipient) => {
    const key = normalizedEmailKey(recipient.email);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

const EmailRecipients = ({agencyId}: Props) => {
  const {request} = usePodRequest();
  const {recipients, isMessageContext} = useOutlookEmailRecipients();
  const [syncableEmailAddresses, setSyncableEmailAddresses] = useState<
    string[]
  >([]);
  const [loxoPeople, setLoxoPeople] = useState<OutlookPersonFromEmailJson[]>(
    []
  );

  useEffect(() => {
    let activeController: AbortController | undefined;
    let latestRequestId = 0;

    const load = () => {
      const requestId = latestRequestId + 1;
      latestRequestId = requestId;
      const emails = [
        ...new Set(
          recipients.map((r) => normalizeEmailAddress(r.email)).filter(Boolean)
        ),
      ];
      if (emails.length === 0) {
        activeController?.abort();
        activeController = undefined;
        setSyncableEmailAddresses([]);
        setLoxoPeople([]);
        return;
      }

      activeController?.abort();
      const controller = new AbortController();
      activeController = controller;

      fetchOutlookPeopleFromEmailAddresses(request, agencyId, emails, {
        signal: controller.signal,
      })
        .then((data: OutlookPeopleFromEmailAddressesJson) => {
          if (!controller.signal.aborted && requestId === latestRequestId) {
            setSyncableEmailAddresses(data.email_addresses_for_sync);
            setLoxoPeople(data.people);
          }
        })
        .catch((error: unknown) => {
          if (
            !controller.signal.aborted &&
            requestId === latestRequestId &&
            !isAbortError(error)
          ) {
            setSyncableEmailAddresses([]);
            setLoxoPeople([]);
          }
        });
    };

    load();

    const removeRefetchListeners = addDebouncedFocusVisibleRefetch(load);

    return () => {
      activeController?.abort();
      removeRefetchListeners();
    };
  }, [agencyId, recipients, request]);

  const syncableEmailSet = useMemo(() => {
    return new Set(syncableEmailAddresses.flatMap(normalizeEmailVariants));
  }, [syncableEmailAddresses]);

  const syncableRecipients = useMemo(() => {
    return dedupeRecipientsByEmail(
      recipients.filter((r) => emailMatchesSet(r.email, syncableEmailSet))
    );
  }, [recipients, syncableEmailSet]);

  const otherRecipients = useMemo(() => {
    const loxoEmails = new Set(
      loxoPeople.flatMap((p) =>
        p.emails.flatMap((e) => normalizeEmailVariants(e.value))
      )
    );
    return syncableRecipients.filter(
      (recipient) => !emailMatchesSet(recipient.email, loxoEmails)
    );
  }, [loxoPeople, syncableRecipients]);

  const recipientCount = useMemo(() => {
    return otherRecipients.length + loxoPeople.length;
  }, [otherRecipients, loxoPeople]);

  if (!isMessageContext) {
    return null;
  }

  return (
    <OuterWrapper>
      <InnerWrapper>
        <TotalCount>
          {recipientCount} Other {recipientCount === 1 ? 'Person' : 'People'} in
          this Email
        </TotalCount>
        <LoxoRecipients
          loxoPeople={loxoPeople}
          syncableEmailAddresses={syncableEmailAddresses}
        />
        <OtherRecipients recipients={otherRecipients} agencyId={agencyId} />
      </InnerWrapper>
      <InboundSyncButton agencyId={agencyId} />
    </OuterWrapper>
  );
};

export default EmailRecipients;
